import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Button,
  Text,
  Icon,
  Grid,
  GridItem,
  Badge,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../hooks/useAuth';

// Custom component for feature item using a check icon
const FeatureItem = ({ text }) => (
  <Flex align="center"  my={1}>
    <Icon as={CheckIcon} color="dark.900" mr={2} boxSize={5} />
    <Text fontSize="sm" color="primary.500">
      {text}
    </Text>
  </Flex>
);

const SubscriptionPage = () => {
  const { subscriptions, loading, userSubscription } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = (sub, isUpgrade = false) => {
    navigate('/subscriptions/checkout', {
      state: {
        subscription: {
          id: sub.id,
          price: sub.unit_amount / 100,
          name: sub.lookup_key,
          isUpgrade,
          current_subscription_id: userSubscription ? userSubscription.subscription_id : null,
        },
      },
    });
  };

  if (!user || (user?.account_type !== 'artist' && user?.account_type !== 'merchant')) {
    return (
      <Box
        py={10}
        px={5}
        bg="dark.100"
        textAlign="center"
        display="flex"
        flexDir="column"
        alignItems="center"
        justifyContent="center"
      >
        <Heading as="h1" mb={8} color="dark.900">
          Subscription Plans
        </Heading>
        <Text color="primary.100" mb={6}>
          These subscription plans are available for artist and merchant accounts.
        </Text>
        {!user && (
          <Button as={RouterLink} to="/login" colorScheme="blue" size="md">
            Go to Login
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box py={10} px={{ base: 4, md: 8 }} bg="dark.200">
      <Heading
        as="h1"
        mb={8}
        textAlign="center"
        color="dark.900"
        fontSize={{ base: '2xl', md: '4xl' }}
      >
        {user?.account_type === 'merchant' ? 'Merchant Subscription' : 'Our Subscription Plans'}
      </Heading>

      {user?.account_type === 'merchant' && (
        <Text textAlign="center" color="primary.100" mb={8} fontSize="lg">
          Ultimate subscription required to create and manage artists
        </Text>
      )}

      {loading ? (
        <Flex justify="center" align="center">
          <Spinner size="xl" color="dark.900" />
        </Flex>
      ) : (
        <Grid
          templateColumns={{ base: '1fr', md: user?.account_type === 'merchant' ? '1fr' : `repeat(${subscriptions.length}, 1fr)` }}
          gap={6}
          justifyItems={user?.account_type === 'merchant' ? 'center' : 'stretch'}
        >
          {subscriptions
            .filter((sub) => {
              // For merchants, only show ultimate subscription
              if (user?.account_type === 'merchant') {
                return sub.lookup_key === 'ultimate_subscription';
              }
              // For artists, show all subscriptions
              return true;
            })
            .map((sub) => {
            const isCurrentSubscription =
              userSubscription && userSubscription.plan === sub.lookup_key;
            const isUpgrade =
              userSubscription && sub.unit_amount > userSubscription.unit_amount;

            const planKey = sub.lookup_key.toLowerCase();

            return (
              <GridItem
                key={sub.id}
                border={user?.account_type === 'merchant' ? "2px solid" : "1px solid"}
                borderColor={user?.account_type === 'merchant' ? "blue.400" : "dark.600"}
                borderRadius="md"
                bg={isCurrentSubscription ? 'dark.100' : 'secondary.500'}
                p={6}
                textAlign="center"
                display="flex"
                flexDir="column"
                justifyContent="space-between"
                maxW={user?.account_type === 'merchant' ? "400px" : "auto"}
                boxShadow={user?.account_type === 'merchant' ? "lg" : "none"}
              >
                <Box>
                  <Heading
                    as="h2"
                    fontSize="xl"
                    mb={2}
                    color="dark.900"
                    textTransform="capitalize"
                  >
                    {sub.lookup_key.replace('_', ' ')}
                    {user?.account_type === 'merchant' && planKey === 'ultimate_subscription' && (
                      <Badge ml={2} colorScheme="blue" variant="solid">
                        Required for Merchants
                      </Badge>
                    )}
                  </Heading>

                  {/* Price Breakdown */}
                  {planKey === 'free_subscription' && (
                    <Flex direction="column">
                      <Text fontSize="lg" fontWeight="bold" mb={1} color="primary.100">
                        $0 per month billed annually
                      </Text>
                      <Text fontSize="sm" mb={4} color="primary.900">
                        Total: $0
                      </Text>
                      <Box mt={4}  alignSelf="center">
                        <FeatureItem text="4 tracks limit" />
                        <FeatureItem text="No free featured credit" />
                        <FeatureItem text="Customer Support" />
                        <FeatureItem text="Platform fee: 20%" />
                      </Box>
                    </Flex>
                  )}               
                  {planKey === 'basic_subscription' && (
                    <Flex direction="column">
                      <Text fontSize="lg" fontWeight="bold" mb={1} color="primary.100">
                        $2.91 per month billed annually
                      </Text>
                      <Text fontSize="sm" mb={4} color="primary.900">
                        Total: $34.99
                      </Text>
                      <Box mt={4} alignSelf="center">
                        <FeatureItem text="7 tracks limit" />
                        <FeatureItem text="1 free featured credit" />
                        <FeatureItem text="Customer Support" />
                        <FeatureItem text="Platform fee: 10%" />
                      </Box>
                    </Flex>
                  )}
                  {planKey === 'plus_subscription' && (
                 <Flex direction="column">
                      <Text fontSize="lg" fontWeight="bold" mb={1} color="primary.100">
                        $6.66 per month billed annually
                      </Text>
                      <Text fontSize="sm" mb={4} color="primary.900">
                        Total: $79.99
                      </Text>
                      <Box mt={4} alignSelf="center">
                        <FeatureItem  text="15 tracks limit" />
                        <FeatureItem text="5 featured credits" />
                        <FeatureItem text="Customer Support" />
                        <FeatureItem text="Platform fee: 10%" />
                      </Box>
                      </Flex>
                  )}
                  {planKey === 'ultimate_subscription' && (
                   <Flex direction="column">
                      <Text fontSize="lg" fontWeight="bold" mb={1} color="primary.100">
                        $20.83 per month billed annually
                      </Text>
                      <Text fontSize="sm" mb={4} color="primary.900">
                        Total: $249.99
                      </Text>
                      <Box mt={4} alignSelf="center">
                        <FeatureItem text="200 tracks limit" />
                        <FeatureItem text="27 featured credits" />
                        <FeatureItem text="Customer Support" />
                        <FeatureItem text="Platform fee: 10%" />
                        {user?.account_type === 'merchant' && (
                          <FeatureItem text="✨ Create & manage artists" />
                        )}
                      </Box>
                      </Flex>
                  )}
                </Box>

                {/* Action Button */}
                <Box>
                  {isCurrentSubscription ? (
                    <Button bg="dark.900" color="dark.100" size="sm" mt={4} isDisabled>
                      Subscribed
                    </Button>
                  ) : isUpgrade ? (
                    <Button
                      colorScheme="blue"
                      size="md"
                      onClick={() => handleSubscribe(sub, true)}
                    >
                      Upgrade
                    </Button>
                  ) : !userSubscription ? (
                    <Button
                      colorScheme="blue"
                      size="md"
                      onClick={() => handleSubscribe(sub, false)}
                    >
                      Subscribe
                    </Button>
                  ) : (
                    <Button colorScheme="gray" size="md" mt={4} isDisabled>
                      Not Eligible
                    </Button>
                  )}
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default SubscriptionPage;